import { useProjectState } from '@common/git/Project/hooks';
import { markLoadPhase } from '@common/metrics/sessionLoad';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { useEffect } from 'react';

import { useMetricsContext } from './useMetricsContext';

/**
 * Adds `projectId` and `teamId` to ambient metrics context for IDE pages.
 * Only use inside `ProjectContextProvider`. Context auto-clears on unmount.
 */
export function MetricsProjectContext() {
  const { projectId, teamId, projectType, projectName, isSqlProject } = useProjectState();
  useMetricsContext({ projectId, teamId });

  // Fire once per project open (projectId becomes available on mount).
  useEffect(() => {
    if (!projectId) return;
    metrics.track({
      eventName: EventName.ui.project.opened,
      eventType: EventType.userAction,
      payload: { projectId, projectType }
    });
  }, [projectId, projectType]);

  // Open milestone: the project's own properties (language, name, branch) have landed, which is
  // what the editor route needs before it can open a socket. Gated on the name rather than on
  // `isGitInfoLoading`: that flag is also false once the query *errors*, and `language` then falls
  // back to python, which would report every SQL project as a pipeline.
  useEffect(() => {
    if (!projectId || !projectName) return;
    markLoadPhase('metadata_loaded', {
      entityType: isSqlProject ? 'sqlProject' : 'pipeline',
      entityId: projectId,
      entityName: projectName
    });
  }, [projectId, projectName, isSqlProject]);

  return null;
}
